import { createRandom, DEMO_SEED, addDays, daysBetween, toISODate, stripApostrophes } from './seededRandom';
import { ASSET_FIELDS } from '@/utils/assetFields';

// Data is generated against a fixed anchor so the seeded RNG always produces
// the same shape, then every date is shifted so the anchor lands on today.
// That keeps "overdue" / "due in 30 days" populated whenever the demo is run.
const ANCHOR_DATE = '2026-08-05';
export const REFERENCE_DATE = toISODate(new Date());
const DATE_SHIFT = daysBetween(ANCHOR_DATE, REFERENCE_DATE);

const shiftDate = (iso) => (iso ? addDays(iso, DATE_SHIFT) : null);
// Naive timestamp (no trailing Z) - read_json_auto infers TIMESTAMPTZ otherwise.
const timestampFor = (isoDate, rnd) =>
  `${isoDate} ${String(rnd.int(7, 18)).padStart(2, '0')}:${String(rnd.int(0, 59)).padStart(2, '0')}:00`;

const svgDataUri = (svg) => `data:image/svg+xml,${encodeURIComponent(svg.replace(/\s+/g, ' ').trim())}`;

const pad = (value, length) => String(value).padStart(length, '0');

const splitWeights = (rnd, count) => {
  const weights = [];
  let total = 0;
  for (let i = 0; i < count; i++) {
    const w = 0.6 + rnd.rand();
    weights.push(w);
    total += w;
  }
  return weights.map((w) => w / total);
};

const floorPlanSvg = (rnd) => {
  const outerPad = 14;
  const width = 400 - outerPad * 2;
  const height = 300 - outerPad * 2;
  const cols = rnd.int(2, 4);
  const rows = rnd.int(2, 3);
  const colWeights = splitWeights(rnd, cols);
  const rowWeights = splitWeights(rnd, rows);
  const corridor = 14;

  let rects = '';
  let y = outerPad;
  for (let r = 0; r < rows; r++) {
    const rowHeight = rowWeights[r] * (height - corridor * (rows - 1));
    let x = outerPad;
    for (let c = 0; c < cols; c++) {
      const colWidth = colWeights[c] * (width - corridor * (cols - 1));
      if (!(r === rows - 1 && c === cols - 1 && rnd.chance(0.35))) {
        rects += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${colWidth.toFixed(1)}" height="${rowHeight.toFixed(1)}" fill="#f7f8f9" stroke="#b6bbc0" stroke-width="3"/>`;
      }
      x += colWidth + corridor;
    }
    y += rowHeight + corridor;
  }

  const coreX = outerPad + width * (0.3 + rnd.rand() * 0.4);
  const coreY = outerPad + height * (0.3 + rnd.rand() * 0.35);

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300" width="400" height="300">
    <rect width="400" height="300" fill="#ffffff"/>
    <rect x="6" y="6" width="388" height="288" fill="none" stroke="#8f969c" stroke-width="4"/>
    ${rects}
    <rect x="${coreX.toFixed(1)}" y="${coreY.toFixed(1)}" width="46" height="34" fill="#e4e7ea" stroke="#8f969c" stroke-width="2"/>
    <line x1="6" y1="${(outerPad + height / 2).toFixed(1)}" x2="394" y2="${(outerPad + height / 2).toFixed(1)}" stroke="#d5d9dd" stroke-width="2"/>
  </svg>`;
};

const photoSvg = (rnd, accent) => {
  const bodyWidth = rnd.int(30, 46);
  const bodyHeight = rnd.int(22, 32);
  const x = Math.round((80 - bodyWidth) / 2);
  const y = Math.round((60 - bodyHeight) / 2);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 60" width="80" height="60">
    <rect width="80" height="60" fill="#eceff1"/>
    <rect x="${x}" y="${y}" width="${bodyWidth}" height="${bodyHeight}" rx="3" fill="${accent}" opacity="0.85"/>
    <circle cx="${x + bodyWidth - 8}" cy="${y + 8}" r="4" fill="#ffffff" opacity="0.8"/>
    <rect x="8" y="48" width="64" height="4" rx="2" fill="#cfd8dc"/>
  </svg>`;
};

const signatureSvg = (rnd) => {
  const points = [];
  for (let i = 0; i <= 8; i++) {
    const x = 8 + i * 8;
    const y = 30 + Math.round((rnd.rand() - 0.5) * 26);
    points.push(`${x},${y}`);
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 60" width="80" height="60">
    <rect width="80" height="60" fill="#ffffff"/>
    <polyline points="${points.join(' ')}" fill="none" stroke="#37474f" stroke-width="2" stroke-linecap="round"/>
    <line x1="8" y1="48" x2="72" y2="48" stroke="#b0bec5" stroke-width="1"/>
  </svg>`;
};

const DRAWING_DEFS = [
  { Id: 'D-1', code: 'A1', building: 'Building A', level: 'Level 1', rooms: ['Plant Room', 'Electrical Riser', 'Lobby', 'Loading Bay'] },
  { Id: 'D-2', code: 'A2', building: 'Building A', level: 'Level 2', rooms: ['Plant Room', 'Electrical Riser', 'Office Zone A', 'Office Zone B'] },
  { Id: 'D-3', code: 'A3', building: 'Building A', level: 'Level 3', rooms: ['Plant Room', 'Electrical Riser', 'Office Zone C'] },
  { Id: 'D-4', code: 'B1', building: 'Building B', level: 'Level 1', rooms: ['Plant Room', 'Switch Room', 'Retail Unit 1', 'Retail Unit 2'] },
  { Id: 'D-5', code: 'B2', building: 'Building B', level: 'Level 2', rooms: ['Plant Room', 'Switch Room', 'Office Zone D'] },
  { Id: 'D-6', code: 'C1', building: 'Building C', level: 'Level 1', rooms: ['Plant Room', 'Switch Room', 'Warehouse Bay'] },
  { Id: 'D-7', code: 'LG', building: 'Basement', level: 'Plant Level', rooms: ['Chiller Hall', 'Boiler Room', 'Pump Room', 'Tank Room', 'Generator Room'] },
  { Id: 'D-8', code: 'RF', building: 'Roof', level: 'Roof Level', rooms: ['Cooling Tower Deck', 'AHU Deck', 'Lift Motor Room', 'Antenna Deck'] },
];

const TEAM_DEFS = [
  { Id: 'T-1', Title: 'Meridian Facilities Services' },
  { Id: 'T-2', Title: 'Northgate Mechanical Ltd' },
  { Id: 'T-3', Title: 'Brightline Electrical Contractors' },
  { Id: 'T-4', Title: 'Cascade Plumbing and Drainage' },
  { Id: 'T-5', Title: 'Vanguard Fire and Security' },
];

const USER_DEFS = [
  { Id: 'U-1', Name: 'Alex Turner' },
  { Id: 'U-2', Name: 'Priya Nair' },
  { Id: 'U-3', Name: 'Marcus Webb' },
  { Id: 'U-4', Name: 'Sofia Almeida' },
  { Id: 'U-5', Name: 'Daniel Okafor' },
  { Id: 'U-6', Name: 'Hannah Lindqvist' },
  { Id: 'U-7', Name: 'Tomas Novak' },
  { Id: 'U-8', Name: 'Grace Mbeki' },
];

const CATEGORY_DEFS = [
  { Id: 'CAT-1', Title: 'Plant Equipment' },
  { Id: 'CAT-2', Title: 'Electrical Distribution' },
  { Id: 'CAT-3', Title: 'Building Services' },
  { Id: 'CAT-4', Title: 'Life Safety' },
  { Id: 'CAT-5', Title: 'Vertical Transport' },
  { Id: 'CAT-6', Title: 'Security Systems' },
];

// Item statuses double as the inspection/asset outcome: a lifecycle record is
// Open until it is closed as Passed / Remedial / Replace, with Monitor as the
// non-terminal "keep an eye on it" state. Commissioned = terminal AND passed.
const STATUS_DEFS = [
  { Id: 'ST-1', Title: 'Open', Color: '#2a78d6', Sort: 10, IsDraft: false, IsInitial: true, IsTerminal: false, IsPassed: false },
  { Id: 'ST-2', Title: 'Monitor', Color: '#fab219', Sort: 20, IsDraft: false, IsInitial: false, IsTerminal: false, IsPassed: false },
  { Id: 'ST-3', Title: 'Passed', Color: '#0ca30c', Sort: 30, IsDraft: false, IsInitial: false, IsTerminal: true, IsPassed: true },
  { Id: 'ST-4', Title: 'Remedial', Color: '#eda100', Sort: 40, IsDraft: false, IsInitial: false, IsTerminal: true, IsPassed: false },
  { Id: 'ST-5', Title: 'Replace', Color: '#d03b3b', Sort: 50, IsDraft: false, IsInitial: false, IsTerminal: true, IsPassed: false },
];

const INITIAL_STATUS = 'ST-1';

// Outcome mix for a completed inspection.
const INSPECTION_OUTCOMES = [
  ['ST-3', 70],  // Passed
  ['ST-4', 12],  // Remedial
  ['ST-2', 8],   // Monitor
  ['ST-5', 5],   // Replace
  ['ST-1', 5],   // still Open
];

const OFFICE_ROOMS = ['Office Zone A', 'Office Zone B', 'Office Zone C', 'Office Zone D', 'Lobby', 'Retail Unit 1', 'Retail Unit 2', 'Warehouse Bay', 'Loading Bay'];

// `count` is the number of PHYSICAL assets of this type; each one generates an
// installation record plus commissioning and inspection records.
const TYPE_DEFS = [
  {
    system: 'Mechanical', type: 'HVAC AHU', code: 'AHU', category: 'Plant Equipment', team: 'T-2', count: 130,
    manufacturers: ['Trane', 'Carrier', 'Daikin', 'Systemair'], valueMin: 18000, valueMax: 85000,
    rooms: ['Plant Room', 'AHU Deck'], accent: '#2a78d6',
  },
  {
    system: 'Mechanical', type: 'Chiller', code: 'CHL', category: 'Plant Equipment', team: 'T-2', count: 40,
    manufacturers: ['York', 'Carrier', 'Trane', 'Daikin'], valueMin: 90000, valueMax: 250000,
    rooms: ['Chiller Hall', 'Cooling Tower Deck', 'Plant Room'], accent: '#2a78d6',
  },
  {
    system: 'Mechanical', type: 'Boiler', code: 'BLR', category: 'Plant Equipment', team: 'T-2', count: 55,
    manufacturers: ['Viessmann', 'Bosch', 'Ideal Commercial', 'Hamworthy'], valueMin: 25000, valueMax: 120000,
    rooms: ['Boiler Room', 'Plant Room'], accent: '#2a78d6',
  },
  {
    system: 'Mechanical', type: 'Pump', code: 'PMP', category: 'Plant Equipment', team: 'T-2', count: 120,
    manufacturers: ['Grundfos', 'Wilo', 'Armstrong', 'KSB'], valueMin: 1500, valueMax: 12000,
    rooms: ['Pump Room', 'Plant Room', 'Tank Room'], accent: '#2a78d6',
  },
  {
    system: 'Electrical', type: 'Distribution Panel', code: 'PNL', category: 'Electrical Distribution', team: 'T-3', count: 110,
    manufacturers: ['Schneider Electric', 'ABB', 'Eaton', 'Siemens'], valueMin: 4000, valueMax: 35000,
    rooms: ['Electrical Riser', 'Switch Room', 'Plant Room'], accent: '#eb6834',
  },
  {
    system: 'Electrical', type: 'Generator', code: 'GEN', category: 'Electrical Distribution', team: 'T-3', count: 28,
    manufacturers: ['Caterpillar', 'Cummins', 'FG Wilson', 'Kohler'], valueMin: 60000, valueMax: 220000,
    rooms: ['Generator Room', 'Plant Room'], accent: '#eb6834',
  },
  {
    system: 'Electrical', type: 'Lighting Circuit', code: 'LTG', category: 'Electrical Distribution', team: 'T-3', count: 150,
    manufacturers: ['Philips', 'Zumtobel', 'Thorn', 'Fagerhult'], valueMin: 500, valueMax: 4500,
    rooms: OFFICE_ROOMS, accent: '#eb6834', quantityMin: 4, quantityMax: 20,
  },
  {
    system: 'Plumbing', type: 'Water Heater', code: 'WHT', category: 'Building Services', team: 'T-4', count: 65,
    manufacturers: ['Rheem', 'Ariston', 'Andrews Water Heaters', 'Lochinvar'], valueMin: 1200, valueMax: 9000,
    rooms: ['Tank Room', 'Plant Room', 'Boiler Room'], accent: '#1baf7a',
  },
  {
    system: 'Plumbing', type: 'Booster Pump Set', code: 'BPM', category: 'Building Services', team: 'T-4', count: 42,
    manufacturers: ['Grundfos', 'Wilo', 'Lowara', 'Pedrollo'], valueMin: 3000, valueMax: 18000,
    rooms: ['Pump Room', 'Tank Room', 'Plant Room'], accent: '#1baf7a',
  },
  {
    system: 'Fire Safety', type: 'Fire Alarm Panel', code: 'FAP', category: 'Life Safety', team: 'T-5', count: 60,
    manufacturers: ['Honeywell', 'Notifier', 'Apollo', 'Kentec'], valueMin: 6000, valueMax: 28000,
    rooms: ['Lobby', 'Electrical Riser', 'Switch Room', 'Plant Room'], accent: '#eda100',
  },
  {
    system: 'Fire Safety', type: 'Sprinkler Zone', code: 'SPZ', category: 'Life Safety', team: 'T-5', count: 140,
    manufacturers: ['Tyco', 'Viking', 'Victaulic', 'Reliable'], valueMin: 2500, valueMax: 20000,
    rooms: OFFICE_ROOMS.concat(['Plant Room']), accent: '#eda100', quantityMin: 2, quantityMax: 12,
  },
  {
    system: 'Fire Safety', type: 'Fire Door', code: 'FDR', category: 'Life Safety', team: 'T-5', count: 180,
    manufacturers: ['Lorient', 'Pyroguard', 'Firestop Systems', 'Hormann'], valueMin: 700, valueMax: 4200,
    rooms: OFFICE_ROOMS.concat(['Plant Room', 'Electrical Riser', 'Switch Room']), accent: '#eda100',
  },
  {
    system: 'Fire Safety', type: 'Fire Damper', code: 'FDM', category: 'Life Safety', team: 'T-5', count: 95,
    manufacturers: ['Actionair', 'Swegon', 'Trox', 'Ruskin'], valueMin: 400, valueMax: 2600,
    rooms: ['Plant Room', 'AHU Deck', 'Electrical Riser', 'Office Zone A', 'Office Zone C'], accent: '#eda100',
  },
  {
    system: 'Vertical Transport', type: 'Passenger Elevator', code: 'ELV', category: 'Vertical Transport', team: 'T-1', count: 35,
    manufacturers: ['Otis', 'Kone', 'Schindler', 'ThyssenKrupp'], valueMin: 80000, valueMax: 240000,
    rooms: ['Lift Motor Room', 'Lobby', 'Plant Room'], accent: '#e87ba4',
  },
  {
    system: 'Security', type: 'CCTV Camera', code: 'CCT', category: 'Security Systems', team: 'T-5', count: 115,
    manufacturers: ['Hikvision', 'Axis Communications', 'Bosch', 'Hanwha Vision'], valueMin: 500, valueMax: 3500,
    rooms: OFFICE_ROOMS.concat(['Antenna Deck']), accent: '#008300',
  },
  {
    system: 'Security', type: 'Access Control Door', code: 'ACS', category: 'Security Systems', team: 'T-5', count: 45,
    manufacturers: ['Paxton', 'HID Global', 'Salto', 'Gallagher'], valueMin: 1500, valueMax: 9000,
    rooms: ['Lobby', 'Loading Bay', 'Retail Unit 1', 'Retail Unit 2', 'Warehouse Bay', 'Office Zone A'], accent: '#008300',
  },
];

const RATING_WEIGHTS = [
  ['Excellent', 15],
  ['Good', 35],
  ['Satisfactory', 25],
  ['Fair', 17],
  ['Poor', 8],
];

const PASS_RATE_BANDS = {
  Excellent: [92, 100],
  Good: [84, 93],
  Satisfactory: [75, 85],
  Fair: [66, 77],
  Poor: [60, 68],
};

const COMMENT_POOL = [
  'Scheduled service completed with no outstanding defects.',
  'Minor vibration noted during the commissioning walkdown.',
  'Access restricted outside building operating hours.',
  'Awaiting manufacturer documentation pack from the supplier.',
  'Filter set replaced during the last maintenance visit.',
  'Isolation valve labelling to be updated at next visit.',
  'Spare parts held in the basement stores.',
];

const buildInstallMonths = () => {
  const months = [];
  for (let i = 0; i < 48; i++) {
    const year = 2023 + Math.floor(i / 12);
    const month = (i % 12) + 1;
    const x = (i - 30) / 8;
    const e = Math.exp(-x);
    const weight = e / Math.pow(1 + e, 2);
    months.push([`${year}-${pad(month, 2)}`, weight]);
  }
  return months;
};

const buildPlan = (rnd, entries, total) => {
  const plan = [];
  entries.forEach(([value, share]) => {
    const count = Math.round(share * total);
    for (let i = 0; i < count; i++) plan.push(value);
  });
  while (plan.length < total) plan.push(entries[0][0]);
  return rnd.shuffle(plan.slice(0, total));
};

const sanitizeTables = (tables) => {
  Object.values(tables).forEach((rows) => {
    rows.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (typeof row[key] === 'string') {
          row[key] = stripApostrophes(row[key]);
        }
        if (row[key] === undefined) {
          row[key] = null;
        }
      });
    });
  });
  return tables;
};

// Every generated item carries every column, so read_json_auto infers one
// stable schema instead of sampling a union of differing key sets.
const ITEM_COLUMN_TEMPLATE = (() => {
  const template = {
    Id: null,
    AssetTemplateId: null,
    AssetClientId: null,
    AssetLocationId: null,
    AssetStatusId: null,
    AssetTeamAssignee: null,
    AssetUserAssignees: null,
    AssetControlledByTeamId: null,
    AssetCreatedByTeamId: null,
    AssetCreatedByUserId: null,
    AssetLastUpdated: null,
  };
  ASSET_FIELDS.forEach((field) => { template[field.tag] = null; });
  return template;
})();

// Whitespace / case variants of an Asset ID that the model must fold together.
const mangleAssetId = (assetRefId, variant) => {
  switch (variant % 3) {
    case 0: return assetRefId.toLowerCase();
    case 1: return ` ${assetRefId} `;
    default: return assetRefId.replace('-', '-  ');
  }
};

export function generateDemoData() {
  const rnd = createRandom(DEMO_SEED);

  const drawings = DRAWING_DEFS.map((def) => {
    const svg = svgDataUri(floorPlanSvg(rnd));
    return {
      Id: def.Id,
      Title: `${def.building} - ${def.level} Floor Plan`,
      Url: svg,
      ThumbnailUrl: svg,
    };
  });

  const locations = [];
  const locationMeta = [];
  let locationSeq = 0;
  DRAWING_DEFS.forEach((def, drawingIndex) => {
    def.rooms.forEach((room) => {
      locationSeq += 1;
      const id = `L-${pad(locationSeq, 3)}`;
      const drawingTitle = drawings[drawingIndex].Title;
      locations.push({
        Id: id,
        Title: `${def.building} - ${def.level} - ${room}`,
        DrawingId: def.Id,
        DrawingTitle: drawingTitle,
        // Live drawings expose plain X / Y (normalised 0-1) - match that.
        X: rnd.decimal(0.08, 0.92, 3),
        Y: rnd.decimal(0.1, 0.9, 3),
      });
      locationMeta.push({ id, room, drawingCode: def.code, building: def.building });
    });
  });

  const teams = TEAM_DEFS.map((team, index) => ({
    Id: team.Id,
    Title: team.Title,
    Code: `TM${pad(index + 1, 2)}`,
    IsActive: true,
  }));

  const users = USER_DEFS.map((user) => ({
    Id: user.Id,
    Name: user.Name,
    Email: `${user.Name.toLowerCase().replace(/ /g, '.')}@rdrive.io`,
    IsActive: true,
  }));

  const team_users = [];
  users.forEach((user, index) => {
    const primary = TEAM_DEFS[index % TEAM_DEFS.length];
    team_users.push({
      TeamId: primary.Id,
      TeamTitle: primary.Title,
      UserId: user.Id,
      UserName: user.Name,
      UserEmail: user.Email,
    });
    if (index % 3 === 0) {
      const secondary = TEAM_DEFS[(index + 2) % TEAM_DEFS.length];
      team_users.push({
        TeamId: secondary.Id,
        TeamTitle: secondary.Title,
        UserId: user.Id,
        UserName: user.Name,
        UserEmail: user.Email,
      });
    }
  });

  const categories = CATEGORY_DEFS.map((category, index) => ({
    Id: category.Id,
    Title: category.Title,
    Code: `CT${pad(index + 1, 2)}`,
    Sort: (index + 1) * 10,
  }));

  const status_templates = STATUS_DEFS.map((status) => ({ ...status }));

  const item_templates = [{
    Id: 'IT-1',
    Title: 'Asset Register',
    Code: 'BI-Asset',
    CategoryTitle: 'Assets',
    SubcategoryTitle: 'Register',
    Type: 'Asset',
  }];

  const current_user = [{
    Id: 'U-1',
    Name: 'Alex Turner',
    Email: 'alex.turner@rdrive.io',
  }];

  const permission_rules = [{
    Id: 'PR-1',
    Name: 'ViewAll',
    Permission: 'ViewAll',
    Scope: 'Project',
    IsAllowed: true,
  }];

  const company = [{
    Id: 'CO-1',
    Name: 'Demo Property Group',
    Code: 'DEMO',
    logoUrl: null,
  }];

  // A small pool of reusable images - one data URI per item would bloat the
  // demo JSON by megabytes at this asset count.
  const photoPool = TYPE_DEFS.map((def) => svgDataUri(photoSvg(rnd, def.accent)));
  const responsePhotoPool = Array.from({ length: 12 }, () => svgDataUri(photoSvg(rnd, '#78909c')));
  const signaturePool = Array.from({ length: 12 }, () => svgDataUri(signatureSvg(rnd)));

  const totalAssets = TYPE_DEFS.reduce((sum, def) => sum + def.count, 0);
  // Where the NEXT inspection falls relative to today, per asset.
  const inspectionPlan = buildPlan(rnd, [
    ['later', 0.52],
    ['due30', 0.08],
    ['due60', 0.07],
    ['due90', 0.08],
    ['overdue', 0.12],
    ['none', 0.13],
  ], totalAssets);

  const installMonths = buildInstallMonths();
  const teamTitleById = {};
  TEAM_DEFS.forEach((team) => { teamTitleById[team.Id] = team.Title; });

  const assets = [];
  const status_history = [];
  let assetSeq = 0;
  let itemSeq = 0;

  // One RDrive Item. `statusId` is its current status; status_history gets the
  // initial row plus, when terminal/monitor, the transition row.
  const pushItem = (fields, { createdDate, statusId, userId, teamId }) => {
    itemSeq += 1;
    const id = `I-${pad(itemSeq, 5)}`;
    const createdAt = timestampFor(createdDate, rnd);

    assets.push({
      ...ITEM_COLUMN_TEMPLATE,
      Id: id,
      AssetTemplateId: 'IT-1',
      AssetClientId: `AST-${itemSeq}`,
      AssetStatusId: statusId,
      AssetCreatedByTeamId: teamId,
      AssetCreatedByUserId: userId,
      AssetControlledByTeamId: teamId,
      AssetLastUpdated: createdAt,
      ...fields,
    });

    status_history.push({
      SubItemId: id,
      StatusId: INITIAL_STATUS,
      CreatedDate: createdAt,
      CreatedByTeamId: teamId,
      CreatedByUserId: userId,
    });

    if (statusId !== INITIAL_STATUS) {
      status_history.push({
        SubItemId: id,
        StatusId: statusId,
        CreatedDate: timestampFor(addDays(createdDate, rnd.int(0, 14)), rnd),
        CreatedByTeamId: teamId,
        CreatedByUserId: userId,
      });
    }

    return id;
  };

  TYPE_DEFS.forEach((def, typeIndex) => {
    const candidateLocations = locationMeta.filter((meta) => def.rooms.includes(meta.room));
    const pool = candidateLocations.length > 0 ? candidateLocations : locationMeta;
    // Life-safety assets are inspected twice a year, everything else annually.
    const intervalDays = def.category === 'Life Safety' ? 180 : 365;

    for (let i = 0; i < def.count; i++) {
      assetSeq += 1;
      const location = rnd.pick(pool);
      const assetRefId = `${def.code}-${location.drawingCode}-${pad(i + 1, 3)}`;
      const manufacturer = rnd.pick(def.manufacturers);
      const installMonth = rnd.weighted(installMonths);
      let installDate = `${installMonth}-${pad(rnd.int(1, 28), 2)}`;
      // Nothing can have been created in the future; the install curve runs
      // past the anchor, so fold those back into recent history.
      if (installDate > ANCHOR_DATE) installDate = addDays(ANCHOR_DATE, -rnd.int(1, 120));

      const teamId = rnd.chance(0.1) ? 'T-1' : def.team;
      const teamTitle = teamTitleById[teamId];
      const owner = rnd.pick(users);
      const secondOwner = rnd.pick(users);
      const userAssignees = owner.Name === secondOwner.Name ? owner.Name : `${owner.Name}, ${secondOwner.Name}`;

      const assetValue = rnd.int(def.valueMin, def.valueMax);
      const installationCost = Math.round(assetValue * rnd.decimal(0.1, 0.3, 3));
      const quantity = def.quantityMin ? rnd.int(def.quantityMin, def.quantityMax) : 1;

      const hasPhoto = rnd.chance(0.8);
      const hasSignature = rnd.chance(0.55);
      const hasSerial = rnd.chance(0.85);
      const hasModel = rnd.chance(0.9);
      const hasQr = rnd.chance(0.6);

      // ~2% of records are captured without an Asset ID; ~3% reference an
      // existing asset with different case/spacing.
      const missingId = rnd.chance(0.02);
      const mangled = !missingId && rnd.chance(0.03);

      const identity = {
        'BI-Asset-ID': missingId ? null : (mangled ? mangleAssetId(assetRefId, assetSeq) : assetRefId),
        'BI-Asset-System': def.system,
        'BI-Asset-Type': def.type,
        'BI-Asset-Category': def.category,
        'BI-Asset-Responsible-Party': teamTitle,
        'BI-Asset-Priority': rnd.chance(0.2),
        AssetLocationId: location.id,
        AssetTeamAssignee: teamTitle,
        AssetUserAssignees: userAssignees,
      };

      // --- Installation record: carries the register data ------------------
      pushItem({
        ...identity,
        'BI-Asset-Manufacturer': manufacturer,
        'BI-Asset-Model': hasModel ? `${def.code}-${rnd.int(1000, 9999)}` : null,
        'BI-Asset-Serial-Number': hasSerial ? `${def.code}${rnd.int(100000, 999999)}` : null,
        'BI-Asset-Install-Date': shiftDate(installDate),
        'BI-Asset-Photo': hasPhoto ? photoPool[typeIndex] : null,
        'BI-Asset-Signature': hasSignature ? rnd.pick(signaturePool) : null,
        'BI-Asset-QR-Code': hasQr ? `QR-${assetRefId}` : null,
        'BI-Asset-Installation-Cost': installationCost,
        'BI-Asset-Quantity': quantity,
        'BI-Asset-Description': `${manufacturer} ${def.type} serving ${location.building} ${location.room}`,
        'BI-Asset-Comments': rnd.chance(0.25) ? rnd.pick(COMMENT_POOL) : null,
      }, {
        createdDate: shiftDate(installDate),
        statusId: rnd.chance(0.93) ? 'ST-3' : 'ST-1',
        userId: owner.Id,
        teamId,
      });

      // --- Commissioning record -------------------------------------------
      const commissioned = rnd.chance(0.85) && addDays(installDate, 5) <= ANCHOR_DATE;
      if (commissioned) {
        const offset = Math.min(rnd.int(5, 60), daysBetween(installDate, ANCHOR_DATE));
        const commissionDate = addDays(installDate, offset);
        pushItem({
          ...identity,
          'BI-Asset-Commission-Date': shiftDate(commissionDate),
          'BI-Asset-Signature': hasSignature ? rnd.pick(signaturePool) : null,
        }, {
          createdDate: shiftDate(commissionDate),
          statusId: 'ST-3',
          userId: rnd.pick(users).Id,
          teamId,
        });
      }

      // --- Inspection records ---------------------------------------------
      // Work backwards from the planned next inspection so the KPI buckets are
      // exactly as intended.
      const plan = inspectionPlan[assetSeq - 1];
      let nextInspectionDate = null;
      if (plan === 'due30') nextInspectionDate = addDays(ANCHOR_DATE, rnd.int(0, 30));
      else if (plan === 'due60') nextInspectionDate = addDays(ANCHOR_DATE, rnd.int(31, 60));
      else if (plan === 'due90') nextInspectionDate = addDays(ANCHOR_DATE, rnd.int(61, 90));
      // Capped at one interval ahead: the next date is set BY the last
      // inspection, so anything further out would imply an inspection that has
      // not happened yet.
      else if (plan === 'later') nextInspectionDate = addDays(ANCHOR_DATE, rnd.int(91, Math.max(92, intervalDays)));
      else if (plan === 'overdue') nextInspectionDate = addDays(ANCHOR_DATE, -rnd.int(1, 240));

      const inspectionCount = nextInspectionDate === null
        ? (rnd.chance(0.35) ? 1 : 0)
        : rnd.int(1, 4);

      // The latest inspection sets the next due date; earlier ones step back
      // by one interval each. Every inspection must already have happened.
      let latestInspectionDate = nextInspectionDate
        ? addDays(nextInspectionDate, -intervalDays)
        : (inspectionCount > 0 ? addDays(ANCHOR_DATE, -rnd.int(30, 400)) : null);
      if (latestInspectionDate && latestInspectionDate > ANCHOR_DATE) {
        latestInspectionDate = addDays(ANCHOR_DATE, -rnd.int(1, 30));
      }

      for (let n = inspectionCount - 1; n >= 0; n -= 1) {
        let inspectionDate = addDays(latestInspectionDate, -n * intervalDays + rnd.int(-10, 10));
        // The jitter can push the most recent inspection past today.
        if (inspectionDate > ANCHOR_DATE) inspectionDate = addDays(ANCHOR_DATE, -rnd.int(0, 5));
        if (inspectionDate < installDate) continue;

        const isLatest = n === 0;
        const rating = rnd.weighted(RATING_WEIGHTS);
        const band = PASS_RATE_BANDS[rating];
        const statusId = rnd.weighted(INSPECTION_OUTCOMES);

        pushItem({
          ...identity,
          'BI-Asset-Inspection-Date': shiftDate(inspectionDate),
          'BI-Asset-Next-Inspection-Date': shiftDate(
            isLatest ? nextInspectionDate : addDays(inspectionDate, intervalDays)
          ),
          'BI-Asset-Rating': rating,
          'BI-Asset-PassRate': rnd.decimal(band[0], band[1], 1),
          'BI-Asset-Response-Photo': rnd.chance(0.6) ? rnd.pick(responsePhotoPool) : null,
          'BI-Asset-Signature': rnd.chance(0.5) ? rnd.pick(signaturePool) : null,
          'BI-Asset-Comments': rnd.chance(0.3) ? rnd.pick(COMMENT_POOL) : null,
        }, {
          createdDate: shiftDate(inspectionDate),
          statusId,
          userId: rnd.pick(users).Id,
          teamId,
        });
      }
    }
  });

  return sanitizeTables({
    assets,
    status_history,
    locations,
    drawings,
    status_templates,
    teams,
    users,
    team_users,
    categories,
    item_templates,
    current_user,
    permission_rules,
    company,
  });
}
